import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "../McqPractice.css";
import Header from "./Header";
import Reviewtestcard from "./ReviewTestCard";
import CustomDropdown from "./CustomDropdown";
import { AuthContext } from "../Hook/AuthProvider";
import { fetchTests, fetchTestExam, fetchTestScorecard } from "../services/testsService";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// by sudz - 260926 - Format marks to 3 decimal places
const formatMarks = (val, decimals = 3) => {
  if (val === null || val === undefined || val === "" || val === "N/A" || val === "—") {
    return val ?? "—";
  }
  const num = Number(val);
  if (isNaN(num)) return val;
  return num.toFixed(decimals);
};

const McqPractice = ({ Check, isSidebarColl }) => {
  const navigate = useNavigate();
  const tooltipRef = useRef(null);
  const fallbackOnEmptyRef = useRef(true);
  const { profileData } = useContext(AuthContext);
  const [isOpenInsight, setIsOpenInsight] = useState(false);
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [clicktaketest, setclicktaketest] = useState(false);
  const [Isclickshare, setIsClickShare] = useState(false);
  const [unlockpro, setUnlockpro] = useState(false);
  const [selectedExamType, setSelectedExamType] = useState(
    () => sessionStorage.getItem("mcq_default_tab") || "pyq"
  );
  const [url, setUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [rawTabTests, setRawTabTests] = useState([]);
  const [hasLoadedTests, setHasLoadedTests] = useState(false);
  const [testsLoading, setTestsLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const tabCacheRef = useRef({});

  // Sorting state - default by created date (ASC) and test_name
  const [sortField, setSortField] = useState("created_date");
  const [sortOrder, setSortOrder] = useState("asc");

  // Hover state for full text preview of truncated test name or subject
  const [textHoverTooltip, setTextHoverTooltip] = useState(null);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder(field === "created_date" ? "desc" : "asc");
    }
  };

  // by sudz:260925 - Column resizing state & handlers
  const [testNameWidth, setTestNameWidth] = useState(30);
  const [subjectWidth, setSubjectWidth] = useState(25);
  const [isResizing, setIsResizing] = useState(false);
  const tableRef = useRef(null);

  const handleStartResize = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startNameW = testNameWidth;
    const startSubjW = subjectWidth;
    const totalShared = startNameW + startSubjW; // 55%
    const tableWidth = tableRef.current ? tableRef.current.getBoundingClientRect().width : 1000;

    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaPercent = (deltaX / tableWidth) * 100;
      let newNameW = Math.min(Math.max(startNameW + deltaPercent, 15), totalShared - 10);
      let newSubjW = totalShared - newNameW;
      setTestNameWidth(parseFloat(newNameW.toFixed(2)));
      setSubjectWidth(parseFloat(newSubjW.toFixed(2)));
    };

    const onMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleResetResize = () => {
    setTestNameWidth(30);
    setSubjectWidth(25);
  };

  useEffect(() => {
    sessionStorage.removeItem("mcq_default_tab");
  }, []);

  // const handleChange = (event: SelectChangeEvent) => {
  //   setAge(event.target.value);
  // };
  const closePopup = () => {
    setclicktaketest(false);
    setUnlockpro(false);
    setIsClickShare(false);
    setIsOpenInsight(false)
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target)
      ) {
        setActiveTooltipId(null); // close popup
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const isProUser = profileData?.user_type === "pro";

  const handleAction = async (test) => {
    sessionStorage.setItem("mcq_default_tab", selectedExamType);
    const isApiShape = Boolean(test.testname?.name);
    const act = isApiShape ? null : test.action;
    // "Take Test" always starts the exam; only "Unlock with PRO" shows the popup
    const canTakeTest = act === "Take Test" || isProUser || test.test_type === "Full";

    if (test.test_id) {
      if (canTakeTest) {
        try {
          setIsLoading(true);
          const examData = await fetchTestExam(test.test_id);
          const examList = Array.isArray(examData)
            ? examData
            : examData?.questions || examData?.items || [];
          const resolvedTestName =
            test.testname?.name || test.testName || "";
          sessionStorage.setItem("test_name", resolvedTestName);
          const durationMinutes =
            examData?.duration_minutes ||
            examData?.exam_duration_minutes ||
            test.details?.exam_duration_minutes ||
            null;
          sessionStorage.setItem(
            "test_exam_data",
            JSON.stringify({
              testId: test.test_id,
              testName: resolvedTestName,
              questions: examList,
              duration_minutes: durationMinutes,
            })
          );
          sessionStorage.setItem("test_id", String(test.test_id));
          // by sudz - 260926 - clear stale session progress and timer so new attempt starts at 0
          sessionStorage.removeItem(`test_progress_${test.test_id}`);
          sessionStorage.removeItem("test_duration_seconds");
          navigate("/test/instructions");
        } catch (error) {
          console.error("Error fetching test exam:", error);
        } finally {
          setIsLoading(false);
        }
      } else {
        setUnlockpro(true);
      }
      return;
    }

    if (canTakeTest) {
      const resolvedTestName =
        test.testname?.name || test.testName || "";
      if (resolvedTestName) {
        sessionStorage.setItem("test_name", resolvedTestName);
      }
      navigate('/test/instructions');
    } else {
      setUnlockpro(true);
      // console.log("Unlock PRO for:", test.testName);
    }
  };

  const handleResult = async (test) => {
    const resolvedTestId = test.test_id || test.id;
    const resolvedTestName =
      test.testname?.name || test.testName || "";
    if (resolvedTestName) {
      sessionStorage.setItem("test_name", resolvedTestName);
    }
    if (resolvedTestId) {
      sessionStorage.setItem("test_id", String(resolvedTestId));
      try {
        setIsLoading(true);
        const userId = localStorage.getItem("user_id") || "";
        const data = await fetchTestScorecard(resolvedTestId, userId);
        sessionStorage.setItem(
          "test_scorecard_data",
          JSON.stringify(data)
        );
      } catch (error) {
        console.error("Error fetching test scorecard:", error);
      } finally {
        setIsLoading(false);
      }
    }
    sessionStorage.setItem("test_review_view", "scorecard");
    navigate("/test/review");
  };


  // Client-side filtering by subject, topic, testtype (sub_type: Full/Sectional), year, month
  const filteredTests = useMemo(() => {
    if (!rawTabTests || rawTabTests.length === 0) return [];
    return rawTabTests.filter((test) => {
      // 1. Filter by test type / sub_type (Full / Sectional)
      if (filters.testtype && filters.testtype.length > 0) {
        const testSubType = (test.test_type || test.sub_type || "").toLowerCase();
        const matches = filters.testtype.some(
          (t) => t.toLowerCase() === testSubType
        );
        if (!matches) return false;
      }

      // 2. Filter by subject
      if (filters.subject && filters.subject.length > 0) {
        const testSubjects = Array.isArray(test.subject)
          ? test.subject.map((s) => s.toLowerCase())
          : test.subject
          ? [test.subject.toLowerCase()]
          : [];
        const matches = filters.subject.some((sub) =>
          testSubjects.some((ts) => ts.includes(sub.toLowerCase()))
        );
        if (!matches) return false;
      }

      // 3. Filter by topic
      if (filters.topic && filters.topic.length > 0) {
        const testTopics = Array.isArray(test.topic)
          ? test.topic.map((t) => t.toLowerCase())
          : test.topic
          ? [test.topic.toLowerCase()]
          : [];
        const matches = filters.topic.some((top) =>
          testTopics.some((tt) => tt.includes(top.toLowerCase()))
        );
        if (!matches) return false;
      }

      // 4. Filter by year
      if (filters.year && filters.year.length > 0) {
        const testYear = test.year
          ? Number(test.year)
          : test.created_at
          ? new Date(test.created_at).getFullYear()
          : null;
        const testName = test.testname?.name || test.testName || "";
        const matches = filters.year.some((y) => {
          const yNum = Number(y);
          return (testYear && testYear === yNum) || testName.includes(String(y));
        });
        if (!matches) return false;
      }

      // 5. Filter by month
      if (filters.month && filters.month.length > 0) {
        const testMonth = test.month
          ? Number(test.month)
          : test.created_at
          ? new Date(test.created_at).getMonth() + 1
          : null;
        const matches = filters.month.some((m) => testMonth === Number(m));
        if (!matches) return false;
      }

      return true;
    });
  }, [rawTabTests, filters]);

  // Client-side sorting across entire filtered set
  const sortedTests = useMemo(() => {
    return [...filteredTests].sort((a, b) => {
      if (sortField === "created_date") {
        const dateA = a.created_at || a.created_date || a.published_at || a.live_from || a.test_id || a.id || 0;
        const dateB = b.created_at || b.created_date || b.published_at || b.live_from || b.test_id || b.id || 0;
        const valA = isNaN(new Date(dateA).getTime()) ? (Number(dateA) || 0) : new Date(dateA).getTime();
        const valB = isNaN(new Date(dateB).getTime()) ? (Number(dateB) || 0) : new Date(dateB).getTime();
        if (valA !== valB) {
          return sortOrder === "asc" ? valA - valB : valB - valA;
        }
        const nameA = (a.testname?.name || a.testName || "").toLowerCase();
        const nameB = (b.testname?.name || b.testName || "").toLowerCase();
        return nameA.localeCompare(nameB);
      }
      if (sortField === "name") {
        const nameA = (a.testname?.name || a.testName || "").toLowerCase();
        const nameB = (b.testname?.name || b.testName || "").toLowerCase();
        return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      }
      if (sortField === "subject") {
        const subA = (Array.isArray(a.subject) ? a.subject.join(", ") : a.subject || "").toLowerCase();
        const subB = (Array.isArray(b.subject) ? b.subject.join(", ") : b.subject || "").toLowerCase();
        return sortOrder === "asc" ? subA.localeCompare(subB) : subB.localeCompare(subA);
      }
      if (sortField === "details") {
        const qA = Number(a.details?.num_questions || parseInt(a.questions, 10) || 0);
        const qB = Number(b.details?.num_questions || parseInt(b.questions, 10) || 0);
        return sortOrder === "asc" ? qA - qB : qB - qA;
      }
      if (sortField === "result") {
        const resA = a.result?.score != null ? Number(a.result.score) : -999;
        const resB = b.result?.score != null ? Number(b.result.score) : -999;
        return sortOrder === "asc" ? resA - resB : resB - resA;
      }
      return 0;
    });
  }, [filteredTests, sortField, sortOrder]);

  // Client-side pagination (10 items per page)
  const totalTests = sortedTests.length;
  const testsList = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedTests.slice(start, start + itemsPerPage);
  }, [sortedTests, currentPage, itemsPerPage]);

  // Reset to page 1 when exam type or filters change
  const isFilterMountRef = useRef(true);
  useEffect(() => {
    if (isFilterMountRef.current) {
      isFilterMountRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [selectedExamType, filters]);

  // Tab fetch with in-memory caching and cache invalidation
  useEffect(() => {
    if (sessionStorage.getItem("mcq_cache_invalidate") === "true") {
      tabCacheRef.current = {};
      sessionStorage.removeItem("mcq_cache_invalidate");
    }

    const cached = tabCacheRef.current[selectedExamType];
    if (cached) {
      setRawTabTests(cached);
      setHasLoadedTests(true);
      setTestsLoading(false);
      return;
    }

    setTestsLoading(true);
    const controller = new AbortController();
    const { signal } = controller;

    const loadTests = async () => {
      try {
        const userId = localStorage.getItem("user_id");
        const data = await fetchTests({
          user_id: userId,
          testname: selectedExamType,
          page: 1,
          page_size: 500,
          signal,
        });

        if (signal.aborted) return;

        const list = Array.isArray(data)
          ? data
          : data?.tests || data?.results || data?.items || [];
        tabCacheRef.current[selectedExamType] = list;
        setRawTabTests(list);
        setHasLoadedTests(true);
      } catch (error) {
        if (signal.aborted) return;
        console.error("Error fetching tests:", error);
        setHasLoadedTests(true);
      } finally {
        if (!signal.aborted) {
          setTestsLoading(false);
        }
      }
    };

    loadTests();

    return () => {
      controller.abort();
    };
  }, [selectedExamType]);

  // Auto fallback from "pyq" to "curated" if no tests found for the selected criteria
  useEffect(() => {
    if (
      selectedExamType === "pyq" &&
      hasLoadedTests &&
      !testsLoading &&
      filteredTests.length === 0 &&
      fallbackOnEmptyRef.current
    ) {
      fallbackOnEmptyRef.current = false; // Prevent fallback loops
      // Sync active filters to "curated" in sessionStorage so CustomDropdown and table stay filtered
      try {
        const stored = JSON.parse(sessionStorage.getItem("mcq_tab_filters") || "{}");
        if (stored["pyq"]) {
          stored["curated"] = { ...stored["pyq"] };
          sessionStorage.setItem("mcq_tab_filters", JSON.stringify(stored));
        }
      } catch (e) {
        console.error("Failed to copy filters to curated tab:", e);
      }
      setSelectedExamType("curated");
    }
  }, [selectedExamType, hasLoadedTests, testsLoading, filteredTests.length]);

  return (
    <div className="mcq-practice-parent">
      <Header
        message={[{ message: "My Tests" }]}
        Check={Check}
        isSidebarColl={isSidebarColl}
      />
      <div className="mcq-container-mcqpractice">
        <div className="my-tests-banner-container">
          <img src="/assets/images/banner_mytests.svg" alt="My Tests" className="my-tests-banner-img" />
        </div>
        {isLoading && (
          <div className="loader-container">
            <div className="loader"></div>
          </div>
        )}


        <div className="table_div_mcqpractice">
            <div className="displaay-top-div">
              <div className="displaay-top-div-left">
                <div className="mcq-tab-group">
                  <button
                    type="button"
                    className={`mcq-tab-btn ${selectedExamType === "pyq" ? "active" : ""}`}
                    onClick={() => setSelectedExamType("pyq")}
                  >
                    PYQs
                  </button>
                  <button
                    type="button"
                    className={`mcq-tab-btn ${selectedExamType === "curated" ? "active" : ""}`}
                    onClick={() => setSelectedExamType("curated")}
                  >
                    Curated
                  </button>
                  {/* <button
                    type="button"
                    className={`mcq-tab-btn ${selectedExamType === "Current Affairs" ? "active" : ""}`}
                    onClick={() => setSelectedExamType("Current Affairs")}
                  >
                    Current Affairs
                  </button> */}
                </div>
                <div className="mcq-filter-row">
                  <CustomDropdown
                    selectedExamType={selectedExamType}
                    onFilterChange={setFilters}
                  />
                </div>
              </div>
              {profileData?.user_type !== "pro" && (
              <div className="mcqpracticw-right-top">
                <div className="mcqpracticw-right-top-header">
                  <div>
                    <img src="/assets/images/mytest-right-side.svg" alt="" />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span className="mcqpracticw-right-top-h">
                      Unlock all tests with PRO
                    </span>
                    <span className="mcqpracticw-right-top-p">
                      Get access to every UPSC test and exclusive insights.
                    </span>
                  </div>
                </div>
                <div className="mcqpracticw-right-top-hrtag"></div>
                <span
                  className="upgrade-pro"
                  onClick={() => {
                    navigate("/Price");
                  }}
                >
                  Upgrate to PRO{" "}
                  <img src="/assets/images/premium-arrow.svg" alt="" />
                </span>
              </div>
              )}
            </div>
              <div className="table-container">
                {hasLoadedTests && totalTests === 0 ? (
                  <div className="no-tests-available">
                    No tests are available
                  </div>
                ) : (
                <div className="table-wrapper">
                  {/* by sudz:260925 - Resizable table columns and proportions */}
                  <table
                    className="test-table"
                    ref={tableRef}
                    style={{
                      "--test-name-width": `${testNameWidth}%`,
                      "--subject-width": `${subjectWidth}%`,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          className={`test-table-td test-name resizable-th sortable-th ${sortField === "name" ? "sorted" : ""}`}
                          style={{ width: `${testNameWidth}%` }}
                        >
                          <div className="th-content">
                            <span
                              className="th-label-sortable"
                              onClick={() => handleSort("name")}
                              title="Click to sort by Test Name"
                            >
                              Test Name
                              <span className="sort-icon-wrapper">
                                {sortField === "name" ? (
                                  sortOrder === "asc" ? " ▲" : " ▼"
                                ) : (
                                  <span className="sort-icon-inactive"> ⇅</span>
                                )}
                              </span>
                            </span>
                            <span
                              className={`col-resizer ${isResizing ? "resizing" : ""}`}
                              onMouseDown={handleStartResize}
                              onDoubleClick={handleResetResize}
                              title="Drag to resize Test Name / Subject columns (Double-click to reset)"
                            />
                          </div>
                        </th>
                        <th
                          className={`test-table-td resizable-th sortable-th ${sortField === "subject" ? "sorted" : ""}`}
                          style={{ width: `${subjectWidth}%` }}
                        >
                          <div className="th-content">
                            <span
                              className="th-label-sortable"
                              onClick={() => handleSort("subject")}
                              title="Click to sort by Subject"
                            >
                              Subject
                              <span className="sort-icon-wrapper">
                                {sortField === "subject" ? (
                                  sortOrder === "asc" ? " ▲" : " ▼"
                                ) : (
                                  <span className="sort-icon-inactive"> ⇅</span>
                                )}
                              </span>
                            </span>
                          </div>
                        </th>
                        <th
                          className={`test-table-td sortable-th ${sortField === "details" ? "sorted" : ""}`}
                          style={{ width: "15%" }}
                          onClick={() => handleSort("details")}
                          title="Click to sort by Questions count"
                        >
                          <div className="th-content">
                            <span className="th-label-sortable">
                              Details
                              <span className="sort-icon-wrapper">
                                {sortField === "details" ? (
                                  sortOrder === "asc" ? " ▲" : " ▼"
                                ) : (
                                  <span className="sort-icon-inactive"> ⇅</span>
                                )}
                              </span>
                            </span>
                          </div>
                        </th>
                        <th
                          className={`test-table-td sortable-th ${sortField === "result" ? "sorted" : ""}`}
                          style={{ width: "15%" }}
                          onClick={() => handleSort("result")}
                          title="Click to sort by Score"
                        >
                          <div className="th-content">
                            <span className="th-label-sortable">
                              Result
                              <span className="sort-icon-wrapper">
                                {sortField === "result" ? (
                                  sortOrder === "asc" ? " ▲" : " ▼"
                                ) : (
                                  <span className="sort-icon-inactive"> ⇅</span>
                                )}
                              </span>
                            </span>
                          </div>
                        </th>
                        <th className="test-table-td" style={{ width: "15%" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {testsLoading ? (
                        Array.from({ length: 8 }).map((_, idx) => (
                          <tr key={`skel-${idx}`} className="skeleton-table-row">
                            <td><div className="skeleton-cell skeleton-cell-lg" /></td>
                            <td><div className="skeleton-cell skeleton-cell-md" /></td>
                            <td><div className="skeleton-cell skeleton-cell-sm" /></td>
                            <td><div className="skeleton-cell skeleton-cell-sm" /></td>
                            <td><div className="skeleton-cell skeleton-cell-md" /></td>
                          </tr>
                        ))
                      ) : testsList.length === 0 ? (
                        <tr>
                          <td colSpan="5" style={{ textAlign: "center", padding: "40px 16px", color: "#64748B", fontSize: "14px" }}>
                            No tests found matching your criteria.
                          </td>
                        </tr>
                      ) : (
                        testsList.map((test) => {
                        const isApiShape = Boolean(test.testname?.name);
                        const testName = isApiShape
                          ? test.testname.name
                          : test.testName;
                        const tags = isApiShape
                          ? test.testname?.tags || []
                          : test.examType
                          ? [test.examType]
                          : [];
                        const subject = isApiShape
                          ? Array.isArray(test.subject)
                            ? test.subject.join(", ")
                            : test.subject
                          : test.subject;
                        const numQuestions = isApiShape
                          ? test.details?.num_questions
                          : test.questions;
                        const duration = isApiShape
                          ? test.details?.exam_duration_minutes
                          : test.duration;
                        const result = isApiShape
                          ? test.result
                          : test.result !== "-"
                          ? test.result
                          : null;
                        const hasResult =
                          result != null && result !== "-";
                        const act = isApiShape ? null : test.action;
                        const rowId = test.test_id || test.id;

                        return (
                        <tr
                          key={rowId}
                          className={test.isPro ? "pro-row" : "normal-row"}
                        >
                          <td>
                            <div className="test-name-cell">
                              <div
                                className="test-name"
                                onMouseEnter={(e) => {
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTextHoverTooltip({
                                    text: testName,
                                    top: rect.top + rect.height / 2,
                                    left: rect.right + 10,
                                  });
                                }}
                                onMouseLeave={() => setTextHoverTooltip(null)}
                              >
                                {testName}
                              </div>
                              <div className="exam-type-container">
                                {tags.map((tag, tagIndex) => (
                                  <span
                                    key={tagIndex}
                                    className={`exam-type ${String(tag).toLowerCase()}`}
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </td>
                          <td
                            className="subject-cell"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTextHoverTooltip({
                                text: subject,
                                top: rect.top + rect.height / 2,
                                left: rect.right + 10,
                              });
                            }}
                            onMouseLeave={() => setTextHoverTooltip(null)}
                          >
                            {subject}
                          </td>
                          <td className="details-cell">
                            <div className="detail-cell-div">{isApiShape ? `${numQuestions} Qs` : numQuestions} <span className="ul-tag-custom"></span> {isApiShape ? `${duration} min` : duration}</div>
                          </td>
                          <td
                            className="result-cell"
                          >
                            {hasResult ? (
                              <span
                                className="result-trigger"
                                onMouseEnter={(e) => {
                                  setActiveTooltipId(rowId);
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setTooltipPos({
                                    top: rect.top + rect.height / 2,
                                    left: rect.right + 10,
                                  });
                                }}
                                onMouseLeave={() => setActiveTooltipId(null)}
                              >
                                <span>{formatMarks(result.score)}</span>{" "}
                                <img
                                  src="/assets/images/view-result.svg"
                                  alt=""
                                />
                                {activeTooltipId === rowId && (
                                  <div
                                    className="score-tooltip"
                                    ref={tooltipRef}
                                    style={{ position: "fixed", top: tooltipPos.top, left: tooltipPos.left }}
                                  >
                                    <div className="score-arrow"></div>

                                    <div className="score-top-row" >
                                      <div className="score-block">
                                        <img
                                          src="/assets/images/makr-metter.svg"
                                          alt=""
                                          className="score-icon"
                                        />
                                        <div>
                                          <div className="label">Your Score</div>
                                          <div className="value">{formatMarks(result.score)}</div>
                                        </div>
                                      </div>

                                      <div className="cutoff-block">
                                        Cutoff {formatMarks(result.cutoff)}
                                      </div>
                                    </div>

                                    <div className="score-middle-row">
                                      <div className="neg-block">
                                        <div className="label">Negative Marks</div>
                                        <div className="neg-value">{formatMarks(result.negative_marks)}</div>
                                      </div>

                                      <div className="attempt-block">
                                        <div className="label">Attempted</div>
                                        <div className="value">{result.attempted} Q</div>
                                      </div>
                                    </div>

                                    {/* <div className="insights-link" onClick={() => { setIsOpenInsight(true); }}>
                                      View AI Insights
                                    </div> */}
                                  </div>
                                )}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td className="actions-cell">
                            {hasResult ? (
                              <div className="after-test-button">
                                <div
                                  className="retake"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleAction(test)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleAction(test);
                                    }
                                  }}
                                >
                                  Re-take
                                </div>
                                <div
                                  className="result-teext"
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => handleResult(test)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      handleResult(test);
                                    }
                                  }}
                                >
                                  Result
                                </div>
                              </div>
                            ) : act === "Take Test" || isProUser || test.test_type === "Full" ? (
                              <button
                                className="action-btn take-test"
                                onClick={() => handleAction(test)}
                              >
                                Take Test
                              </button>
                            ) : (
                              <div className="unlock-pro-gradient">
                                <div
                                  className="unlock-pro"
                                  onClick={() => handleAction(test)}
                                >
                                  <span>
                                    <img
                                      src="/assets/images/lock-closed-icon-mcq.svg"
                                      alt=""
                                    />
                                  </span>
                                  Unlock with PRO
                                </div>
                              </div>
                            )}

                            {/* Share button temporarily commented out until full sharing flow is implemented */}
                            {/* <button
                              className="share-btn"
                              title="Share test"
                              onClick={() => {
                                const rowId = test.test_id || test.id;
                                const shareLink = test.link || `${window.location.origin}/tests?test_id=${rowId}`;
                                setIsClickShare(true);
                                setUrl(shareLink);
                              }}
                            >
                              <span className="share-icon">
                                <img
                                  src="/assets/images/share-icon.svg"
                                  alt="Share"
                                />
                              </span>
                            </button> */}
                          </td>
                        </tr>
                        );
                      })
                      )}
                    </tbody>
                  </table>
                </div>
                )}
                {hasLoadedTests && (totalTests === 0 || testsLoading) ? null : (
                <div className="table-footer">
                  <div className="total-count">{totalTests} Tests</div>
                  <div className="pagination">
                    <span className="page-info">
                      Showing {(currentPage - 1) * itemsPerPage + 1}-
                      {Math.min(currentPage * itemsPerPage, totalTests)}
                    </span>
                    <button
                      className="page-btn"
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                      title="Previous page"
                    >
                      <ChevronLeft size={15} strokeWidth={2.2} />
                    </button>
                    <button
                      className="page-btn"
                      onClick={() => setCurrentPage((prev) => prev + 1)}
                      disabled={currentPage * itemsPerPage >= totalTests}
                      title="Next page"
                    >
                      <ChevronRight size={15} strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
                )}
              </div>
              {textHoverTooltip && (
                <div
                  className="table-cell-hover-tooltip"
                  style={{
                    position: "fixed",
                    top: textHoverTooltip.top,
                    left: textHoverTooltip.left,
                  }}
                >
                  <div className="tooltip-arrow-left"></div>
                  <div className="tooltip-content-text">{textHoverTooltip.text}</div>
                </div>
              )}
            </div>
        </div>
      {clicktaketest && (
        <div
          className={`payment-success-overlay ${clicktaketest ? "p-active" : ""}`}
          aria-hidden={!clicktaketest}
        >
          <div
            className="payment-success-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="paymentSuccessTitle"
          >
            <div className="payment-success-wrapper">
              <div className="mcqpractice-taketest">
                <div className="payment-success-left">
                  <div className="mcqpractice-image-wrap">
                    <img
                      src="/assets/images/pop-up-model-img.svg"
                      alt=""
                      className="take-test-image"
                    />
                    <img
                      src="/assets/images/oval-color.svg"
                      alt=""
                      className="oval-img"
                    />
                    <img
                      src="/assets/images/all-the-best-img.svg"
                      alt=""
                      className="take-allthebest-image"
                    />
                  </div>
                </div>
                <div className="payment-success-right-model">
                  <button
                    className="payment-success-close"
                    onClick={closePopup}
                    aria-label="Close popup"
                  >
                    <img src="/assets/images/close-icon-model.svg" alt="" />
                  </button>
                  <div className="model-tag">
                    <span className="exam-type gs">GS</span>
                    <span className="exam-type prelim">prelims</span>
                  </div>
                  <div className="payment-success-right-body">
                    <div className="payment-success-right-body-header">
                      <span className="payment-success-right-body-header-h">
                        Sectional Test PYQ
                      </span>
                      <span className="payment-success-right-body-header-p">
                        Modern History - Acts (1979 - 2024)
                      </span>
                    </div>
                    <div className="payment-success-right-body-body">
                      <div className="instartion-detail-div">
                        <span className="instartion-detail-div-h">80</span>
                        <span className="instartion-detail-div-p">Questions</span>
                      </div>
                      <div className="horizonatl-line"></div>
                      <div className="instartion-detail-div">
                        <span className="instartion-detail-div-h">90 min</span>
                        <span className="instartion-detail-div-p">Duration</span>
                      </div>
                      <div className="horizonatl-line"></div>
                      <div className="instartion-detail-div">
                        <span className="instartion-detail-div-h">80</span>
                        <span className="instartion-detail-div-p">Max Score</span>
                      </div>
                    </div>
                    <div className="Marking-Scheme">
                      <span className="making-scheme">Marking Scheme :</span>
                      <div className="making-scheme-marking">
                        <span className="correct-mark">+2 Correct</span>
                        <span className="wrong-mark">-0.67 Incorrect</span>
                      </div>
                    </div>
                    <div className="start-test" onClick={() => { sessionStorage.setItem("mcq_default_tab", selectedExamType); navigate('/test/instructions'); }}>start test</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {unlockpro && (
        <div
          className="mcq-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Unlock with PRO"
          onClick={(e) => {
            if (e.target === e.currentTarget) closePopup();
          }}
        >
          <div className="mcqpractice-upgrade-pro">
            <button
              type="button"
              className="mcq-modal-close"
              onClick={closePopup}
              aria-label="Close popup"
            >
              <X size={18} strokeWidth={2.2} />
            </button>
            <div className="img-div-mcq">
              <img src="/assets/images/cloudy.svg" alt="" />
              <img
                src="/assets/images/recharge.svg"
                alt=""
                className="recharge-img"
              />
            </div>
            <div className="mcqpractice-upgrade-pro-two">
              <span className="mcqpractice-upgrade-pro-two-h">
                Unlock with PRO
              </span>
              <span className="mcqpractice-upgrade-pro-two-p">
                Access the full UPSC test library, result insights, and
                detailed performance analysis.
              </span>
            </div>
            <div className="mcqpractice-upgrade-pro-three">
              <span className="mcqpractice-upgrade-pro-three-text">
                <img src="/assets/images/unlock-all-test-access.svg" alt="" />
                Unlock all test access
              </span>
              <span className="mcqpractice-upgrade-pro-three-hr-tag"></span>
              <span className="mcqpractice-upgrade-pro-three-text">
                <img
                  src="/assets/images/samart-progress-tracking.svg"
                  alt=""
                />
                Smart progress tracking
              </span>
              <span className="mcqpractice-upgrade-pro-three-hr-tag"></span>
              <span className="mcqpractice-upgrade-pro-three-text">
                <img src="/assets/images/ai-performane-insight.svg" alt="" />
                AI Performance insights
              </span>
            </div>
            <div
              className="upgrade-pro-button-gradent-border"
              onClick={() => {
                navigate("/Price");
              }}
            >
              <div className="upgrade-pro-button">Upgrade to PRO</div>
            </div>
          </div>
        </div>
      )}

      {Isclickshare && (
        <div
          className={`payment-success-overlay ${Isclickshare ? "p-active" : ""}`}
          aria-hidden={!Isclickshare}
        >
          <div
            className="payment-success-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="paymentSuccessTitle"
          >
            <div className="payment-success-wrapper">
              <div className="mcqpractice-share">
                <div className="mcqpractice-share-top">
                  <span className="share-test-text">Share a test</span>
                  <div
                    className="close-img-icon"
                    onClick={closePopup}
                    aria-label="Close popup"
                  >
                    <img src="/assets/images/close-icon-model.svg" alt="" />
                  </div>
                </div>
                <div className="mcqpractice-share-buttom">
                  <span className="mcqpractice-share-buttom-text">
                    Just attempted this UPSC test on Yooki. Sharing it in case you
                    want to give it a shot!
                  </span>
                  <span className="mcqpractice-share-buttom-url">{url}</span>
                  <span
                    className="mcqpractice-share-buttom-copy-button"
                    onClick={() => {
                      navigator.clipboard.writeText(url);
                    }}
                  >
                    <img src="/assets/images/copy-svgrepo-com.svg" alt="" />
                    copy
                  </span>
                </div>
                <span className="hr-tag-share-model"></span>
                <div className="social-media-share">
                  <div>
                    {" "}
                    <img src="/assets/images/wp-img.svg" alt="" />
                    <span className="social-text">WhatsApp</span>
                  </div>
                  <div>
                    <img src="/assets/images/insta-img.svg" alt="" />
                    <span className="social-text">Instagram</span>
                  </div>
                  <div>
                    <img src="/assets/images/msg-img.svg" alt="" />
                    <span className="social-text">Messages</span>
                  </div>
                  <div>
                    <img src="/assets/images/x-img.svg" alt="" />
                    <span className="social-text">XDirects</span>
                  </div>
                  <div>
                    <img src="/assets/images/more-img.svg" alt="" />
                    <span className="social-text">More</span>{" "}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isOpenInsight && (
        <div
          className={`payment-success-overlay ${isOpenInsight ? "p-active" : ""}`}
          aria-hidden={!isOpenInsight}
        >
          <div
            className="payment-success-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="paymentSuccessTitle"
          >
            <div className="payment-success-wrapper">
              <div className="mcqpractice-share">
                <div className="mcqpractice-share-top">
                  <span className="share-test-text">Share a test</span>
                  <div
                    className="close-img-icon"
                    onClick={closePopup}
                    aria-label="Close popup"
                  >
                    <img src="/assets/images/close-icon-model.svg" alt="" />
                  </div>
                </div>
                <div className="mcqpractice-share-buttom">
                  <span className="mcqpractice-share-buttom-text">
                    Just attempted this UPSC test on Yooki. Sharing it in case you
                    want to give it a shot!
                  </span>
                  <span className="mcqpractice-share-buttom-url">{url}</span>
                  <span
                    className="mcqpractice-share-buttom-copy-button"
                    onClick={() => {
                      navigator.clipboard.writeText(url);
                    }}
                  >
                    <img src="/assets/images/copy-svgrepo-com.svg" alt="" />
                    copy
                  </span>
                </div>
                <span className="hr-tag-share-model"></span>
                <div className="social-media-share">
                  <div>
                    {" "}
                    <img src="/assets/images/wp-img.svg" alt="" />
                    <span className="social-text">WhatsApp</span>
                  </div>
                  <div>
                    <img src="/assets/images/insta-img.svg" alt="" />
                    <span className="social-text">Instagram</span>
                  </div>
                  <div>
                    <img src="/assets/images/msg-img.svg" alt="" />
                    <span className="social-text">Messages</span>
                  </div>
                  <div>
                    <img src="/assets/images/x-img.svg" alt="" />
                    <span className="social-text">XDirects</span>
                  </div>
                  <div>
                    <img src="/assets/images/more-img.svg" alt="" />
                    <span className="social-text">More</span>{" "}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default McqPractice;